import actions from '@/stores/product-actions.js'
import {
  findVariantById,
  findVariantIdByGender,
  createRouteColors,
} from '@/stores/product-store-helpers.js'

const FIRST_REVIEW_PAGE = 1
const LOW_STOCK_LIMIT = 12
export const REVIEWS_PER_PAGE = 3
export const BEST_REVIEW_RATING = 5
export const WORST_REVIEW_RATING = 1
export const INITIAL_SLIDE_INDEX = 0
export const TOPGALLERY_IMAGE_OPTIONS = {
  format: 'jpg',
  backgroundColor: 'f7f7f7',
}

export const PRODUCT_TYPES = {
  shoes: 'shoes',
  apparel: 'apparel',
  accessories: 'accessories',
  giftCards: 'gift_cards',
}

export const PRODUCT_IMAGE_TYPES = {
  noBordersTall: 'no-borders-tall',
  noBordersWide: 'no-borders-wide',
  allBorders: 'all-borders',
  topBottomBorders: 'top-bottom-borders',
}

export const POSSIBLE_NO_BORDER_VALUES = [
  PRODUCT_IMAGE_TYPES.noBordersTall,
  PRODUCT_IMAGE_TYPES.noBordersWide,
  null,
]

export const GENDERS = {
  mens: 'mens',
  womens: 'womens',
  unisex: 'unisex',
}

GENDERS.default = GENDERS.mens

/**
 * Returns true if there is stock but is below the stock limit that we have set
 *
 * @param {Number} stock - Stock left for a selected sku
 */
export function isLowStock(stock) {
  return !!(stock && stock <= LOW_STOCK_LIMIT)
}

export default {
  namespaced: true,
  state: {
    currentGalleryItemIndex: 0,
    currentVariantIdInternal: -1,
    hasFetchedFirstReviews: false,
    isAllItemsExpanded: false,
    isFastConnection: true,
    isGalleryOpen: false,
    isRouteUpdatingBlocked: false,
    hasTopGalleryMounted: false,
    hasStickyMenuMounted: false,
    hasTopBannerMounted: false,
    isForcingLoadingIndicator: false,
    isLoaded: false,
    isLoadingMoreReviews: false,
    productGroup: { productData: { variants: [] } },
    reviews: [],
    reviewRatingAverage: 0,
    reviewTotal: 0,
    reviewPageLoaded: 0,
    scrollPosition: 0,
    selectedGender: GENDERS.default,
    slideIndex: INITIAL_SLIDE_INDEX,
    slideResetter: undefined,
    recommendationSettings: {
      color: undefined,
      position: undefined,
    },
    isSizeInfoVisible: false,
  },

  mutations: {
    addReviews(state, payload) {
      state.reviews.push(...payload)
      state.reviewPageLoaded++
    },

    blockRouteUpdating(state) {
      state.isRouteUpdatingBlocked = true
    },

    openGallery(state, payload) {
      state.isGalleryOpen = payload
    },

    setTopGalleryMounted(state, payload) {
      state.hasTopGalleryMounted = payload
    },

    setTopBannerMounted(state, payload) {
      state.hasTopBannerMounted = payload
    },

    setStickyMenuMounted(state, payload) {
      state.hasStickyMenuMounted = payload
    },

    setAllItemsExpanded(state, payload) {
      state.isAllItemsExpanded = payload
    },

    setConnectionSpeed(state, payload) {
      state.isFastConnection = payload
    },

    setCurrentCaptionIndex(state, payload) {
      state.lastCaptionIndex = state.currentCaptionIndex
      state.currentCaptionIndex = payload
    },

    setCurrentGalleryItemIndex(state, payload) {
      state.currentGalleryItemIndex = payload
    },

    /**
     * If you need to get the current variant id in a component, you should rather get the id from
     * the "currentVariant" getter which comes with a fallback to the first variant if no variant
     * is matching "currentVariantIdInternal" (it would be safer since "currentVariantIdInternal"
     * equals to "-1" until "setCurrentVariantId" is called).
     */
    setCurrentVariantId(state, payload) {
      state.currentVariantIdInternal = payload
    },

    setGender(state, payload) {
      state.selectedGender = payload
    },

    setHasFetchedFirstReviews(state, payload) {
      state.hasFetchedFirstReviews = payload
    },

    setIsLoadingMoreReviews(state, payload) {
      state.isLoadingMoreReviews = payload
    },

    setProductGroup(state, payload) {
      state.productGroup = payload
      state.isLoaded = true
    },

    setReviewRatingAverage(state, payload) {
      state.reviewRatingAverage = payload || 0
    },

    setReviews(state, payload) {
      state.reviews = payload
      state.reviewPageLoaded = FIRST_REVIEW_PAGE
    },

    setReviewTotal(state, payload) {
      state.reviewTotal = payload || 0
    },

    setScrollposition(state, payload) {
      state.scrollPosition = payload
    },

    setSlideIndex(state, payload) {
      state.slideIndex = payload
    },

    resetSlideIndex(state) {
      // To prevent the slide animation on reset
      // i.e. [3, false] would go to index 3 without animation
      // See vue-carousel documentation of `navigateTo` for details
      state.slideResetter = [INITIAL_SLIDE_INDEX, false]
    },

    editRecommendationSettings(state, payload) {
      state.recommendationSettings = {
        color: payload.color || state.recommendationSettings.color,
        position: payload.position || state.recommendationSettings.position,
      }
    },

    setSizeInfo(state, payload) {
      state.isSizeInfoVisible = payload
    },

    setIsForcingLoadingIndicator(state, payload) {
      // Use externally to control when to stop showing the loading indicator
      state.isForcingLoadingIndicator = payload
    },
  },
  actions: actions,

  getters: {
    currentVariant: (state, getters) => {
      const findVariant = (variants, id) => findVariantById(variants, id) || variants[0] || {}
      return {
        productName: getters.productName,
        ...findVariant(getters.product.variants || [], state.currentVariantIdInternal),
      }
    },
    currentHighlights: (state, getters) =>
      state.isLoaded ? getters.currentVariant.highlights || [] : [],
    currentSpreeProduct: (state, getters) =>
      state.isLoaded ? getters.currentVariant.spreeProduct : null,
    firstMaleVariantId: (state, getters) =>
      findVariantIdByGender(getters.product.variants, GENDERS.mens),
    firstFemaleVariantId: (state, getters) =>
      findVariantIdByGender(getters.product.variants, GENDERS.womens),
    isVisible: (state, getters) =>
      state.isLoaded && !state.isForcingLoadingIndicator && getters.hasLazyComponentsMounted,
    mainVariant: (_state, getters) =>
      findVariantById(getters.product.variants, getters.firstMaleVariantId) || {},
    mainVariantColors: (state, getters) =>
      state.isLoaded ? createRouteColors(getters.mainVariant.color) : '',
    product: state => state.productGroup.productData,
    productName: (_state, getters) => getters.product.name,
    productGroupSlug: state => state.productGroup.slug,
    selectedColors: (state, getters) =>
      state.isLoaded ? createRouteColors(getters.currentVariant.color) : '',
    hasLazyComponentsMounted: (state, getters) =>
      state.hasTopGalleryMounted &&
      state.hasStickyMenuMounted &&
      getters.hasDynamicComponentsMounted,
    hasDynamicComponentsMounted: (state, getters) =>
      getters.isRaffle && !getters.hasRaffleCountdownEnded ? state.hasTopBannerMounted : true,
    isRaffle: (state, getters) => !!getters.currentSpreeProduct?.isRaffle,
    isGiftCard: (state, getters) =>
      getters.currentSpreeProduct?.productType === PRODUCT_TYPES.giftCards,
    topBannerImageUrlMobile: (state, getters) => getters.getTopBannerUrl('mediaMobileUrl'),
    topBannerImageUrlDesktop: (state, getters) => getters.getTopBannerUrl('mediaDesktopUrl'),
    topBannerVideoUrlMobile: (state, getters) => getters.getTopBannerUrl('videoMobileUrl'),
    topBannerVideoUrlDesktop: (state, getters) => getters.getTopBannerUrl('videoDesktopUrl'),
    getTopBannerUrl: (state, getters) => urlPropName =>
      getters.currentVariant?.topBanner?.[urlPropName] || '',
    raffleExpiresAt: (state, getters) => getters.currentSpreeProduct?.raffleExpiresAt || '',
    hasRaffleCountdownEnded: (state, getters, rootState) =>
      Date.parse(getters.raffleExpiresAt) - rootState.page.currentTime < 0,
  },
}
